import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '@wa/shared';
import { AppShell } from '@/app/AppShell';
import { BillingPage } from '@/pages/BillingPage';
import { ShopPage } from '@/pages/ShopPage';
import { BroadcastPage } from '@/pages/BroadcastPage';
import { ScheduledBroadcastsPage } from '@/pages/ScheduledBroadcastsPage';
import { TemplatesPage } from '@/templates/TemplatesPage';
import { TemplateEditorPage } from '@/templates/TemplateEditorPage';
import { CustomersPage } from '@/customers/CustomersPage';
import { HomePage } from '@/pages/HomePage';
import { InboxPage } from '@/pages/InboxPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { SettingsIndex, SettingsLayout, TeamRedirect } from '@/settings/SettingsLayout';
import { SettingsSectionRoute } from '@/settings/SettingsSectionRoute';
import { BotsPage } from '@/pages/BotsPage';
import { LoginPage } from '@/pages/LoginPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';

export function App() {
  const { user, isBootstrapping } = useAuth();

  // Waiting on the silent refresh. Rendering the login form here would flash it at
  // every reload for someone who is already signed in.
  if (isBootstrapping) {
    return <div className="grid h-full place-items-center text-body text-(--color-muted)">Loading…</div>;
  }

  if (!user) {
    // The reset link lands here signed out, which is the only state anybody
    // following it is ever in.
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/home" element={<HomePage />} />

        {/* Renamed to match the product this is replicating, which calls them
            Chats and Customers. The old addresses still work: somebody's
            bookmark should not 404 because a label changed. */}
        <Route path="/chats" element={<InboxPage />} />
        <Route path="/inbox" element={<Navigate to="/chats" replace />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:id" element={<CustomersPage />} />
        <Route path="/contacts" element={<Navigate to="/customers" replace />} />
        {/* Broadcast lists, the reference's model. Campaigns was a table of
            sends with a builder; it redirects, so a bookmark still lands. */}
        <Route path="/broadcasts" element={<BroadcastPage />} />
        <Route path="/broadcasts/:listId" element={<BroadcastPage />} />
        <Route path="/scheduled-broadcasts" element={<ScheduledBroadcastsPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/templates/:id" element={<TemplatesPage />} />
        <Route path="/templates/new" element={<TemplateEditorPage />} />
        <Route path="/templates/:id/edit" element={<TemplateEditorPage />} />
        <Route path="/campaigns" element={<Navigate to="/broadcasts" replace />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/bots" element={<BotsPage />} />
        <Route path="/bots/:id" element={<BotsPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/shop" element={<ShopPage />} />

        {/* Settings: one entry on the rail, a column of sections inside it, every
            section its own address. The Team screen this replaces was five tabs
            reached from three rail entries; both of its old addresses redirect,
            because a bookmark should not 404 because the navigation changed. */}
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<SettingsIndex />} />
          <Route path=":section" element={<SettingsSectionRoute />} />
        </Route>
        <Route path="/team" element={<TeamRedirect />} />
        <Route path="/routing" element={<Navigate to="/settings/routing" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}
